import React, { useEffect, useState } from 'react';
import { setExclusion } from '../../utils/api_integrations';

// ---------------------------------------------------------------- #
// WizardFrame
// ---------------------------------------------------------------- #
//
// The shared modal every Integrations Register wizard sits in:
//   - a header with the provider logo, the park's name (or the wizard title
//     when it was not opened from a park), a status pill and a close button;
//   - a slim stepper: one bar per step and a "Step 2 of 4: Accounts" caption;
//   - the step content, with any error shown above it;
//   - a footer strip: "Not needed for this park" switches on the left, then
//     Back and the primary button on the right.
// It holds no wizard state beyond those switches; each wizard owns its steps.
//
// The switches save straight away through exclusions/set. Staff viewing as a
// customer may use them too; only connecting is the customer's job.
//
// Props:
//   title              Provider heading, for example "Google"
//   logoSrc            Optional logo shown in the header
//   register           The `data` of GET register, to read the park's cells
//   parkiId            The park the wizard was opened from, or null
//   products           Product keys this wizard covers, for example ['facebook', 'instagram']
//   onNotNeededChange  Called with (productKey, notNeeded) after a switch saves
//   steps              Step labels, for example ['Connect', 'Accounts', 'Map parks', 'Review']
//   step               The current step, 1-based
//   maxReachedStep     The furthest step reached; bars up to it are clickable
//   onStepChange       Called with the step number when the user picks a step or Back
//   error              Plain-English error text, or null
//   dirty              True when there are unsaved changes; closing then asks first
//   onClose            Called when the user closes the wizard (after any confirm)
//   primaryLabel       Text of the right-hand button; leave out to hide it
//   onPrimary          Called when the right-hand button is pressed
//   primaryDisabled    Disables the right-hand button
//   busy               Shows a spinner on the right-hand button and disables both buttons
//   hideBack           Hides the Back button even when not on step 1
//   footerStart        Optional extra content after the switches, for example a small link
//   children           The step content

// ---------------------------------------------------------------- #
// Constants
// ---------------------------------------------------------------- #

/** Message shown when the user closes a wizard with unsaved changes. */
const DISCARD_CHANGES_PROMPT = 'Discard changes?';

/** The words shown for each product in the footer switches. */
const PRODUCT_LABELS = {
  pms: 'PMS',
  bookkeeping: 'Bookkeeping',
  analytics: 'Analytics',
  business: 'Business',
  facebook: 'Facebook',
  instagram: 'Instagram'
};

/** Status pill text and colour class for each cell state. */
const STATUS_PILLS = {
  'needs-action': { label: 'Needs action', toneClass: 'is-action' },
  'contact-support': { label: 'Contact support', toneClass: 'is-action' },
  'in-review': { label: 'In review', toneClass: 'is-waiting' },
  'gap': { label: 'Not set up', toneClass: 'is-waiting' },
  'complete': { label: 'Connected', toneClass: 'is-complete' },
  'not-needed': { label: 'Not needed', toneClass: 'is-muted' },
  'not-in-plan': { label: 'Not in plan', toneClass: 'is-muted' }
};

/** When a wizard covers two products, the pill shows the state that most needs attention. */
const STATUS_PRIORITY = ['needs-action', 'contact-support', 'in-review', 'gap', 'complete', 'not-needed', 'not-in-plan'];

/** Cell states whose not-needed switch cannot be used (the register refuses writes there). */
const LOCKED_STATES = ['not-in-plan', 'contact-support'];

// ---------------------------------------------------------------- #
// Helpers
// ---------------------------------------------------------------- #

/**
 * Find a park in the register by id, or null.
 */
const findPark = (register, parkiId) => {
  if (parkiId === null || parkiId === undefined) {
    return null;
  }
  for (const park of register?.parks || []) {
    if (String(park.parkiId) === String(parkiId)) {
      return park;
    }
  }
  return null;
};

/**
 * A cell's state once any switch flipped in this wizard is taken into account.
 */
const getEffectiveState = (cell, overrideNotNeeded) => {
  // No switch flipped: the register's own state
  const hasOverride = overrideNotNeeded !== undefined;
  if (!hasOverride) {
    return cell.state;
  }

  // Switched to not needed
  if (overrideNotNeeded) {
    return 'not-needed';
  }

  // Switched back to needed: a not-needed cell becomes a gap, any other state stays
  if (cell.state === 'not-needed') {
    return 'gap';
  }
  return cell.state;
};

// ---------------------------------------------------------------- #
// The frame
// ---------------------------------------------------------------- #

/**
 * The shared modal, header, stepper and footer used by every integrations wizard.
 */
const WizardFrame = ({
  title,
  logoSrc = null,
  register = null,
  parkiId = null,
  products = [],
  onNotNeededChange,
  steps = [],
  step = 1,
  maxReachedStep = 1,
  onStepChange,
  error = null,
  dirty = false,
  onClose,
  primaryLabel = null,
  onPrimary,
  primaryDisabled = false,
  busy = false,
  hideBack = false,
  footerStart = null,
  children
}) => {
  // Switches flipped in this wizard, keyed by product, and the one being saved
  const [notNeededOverrides, setNotNeededOverrides] = useState({});
  const [savingProduct, setSavingProduct] = useState(null);
  const [switchError, setSwitchError] = useState(null);

  // ---------------------------------------------------------------- #
  // Closing
  // ---------------------------------------------------------------- #

  /**
   * Close the wizard, asking first when there are unsaved changes.
   */
  const requestClose = () => {
    // Ask before throwing away unsaved changes
    if (dirty) {
      const userAgreed = window.confirm(DISCARD_CHANGES_PROMPT);
      if (!userAgreed) {
        return;
      }
    }

    // Hand the close to the wizard
    if (onClose) {
      onClose();
    }
  };

  // Close on the Escape key, the same as the user switcher modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        requestClose();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  });

  // Stop the page behind the modal from scrolling while it is open
  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, []);

  // ---------------------------------------------------------------- #
  // The park, its cells and the status pill
  // ---------------------------------------------------------------- #

  // The park this wizard was opened from, and one entry per product it covers
  const park = findPark(register, parkiId);
  const productRows = [];
  if (park) {
    for (const productKey of products) {
      const cell = park.cells?.[productKey];
      if (!cell) {
        continue;
      }
      const effectiveState = getEffectiveState(cell, notNeededOverrides[productKey]);
      productRows.push({ productKey: productKey, cell: cell, effectiveState: effectiveState });
    }
  }

  // The pill shows the state that most needs attention across the products
  let pill = null;
  let bestPriority = STATUS_PRIORITY.length;
  for (const row of productRows) {
    const priority = STATUS_PRIORITY.indexOf(row.effectiveState);
    const knownState = priority >= 0;
    if (knownState && priority < bestPriority) {
      bestPriority = priority;
      pill = STATUS_PILLS[row.effectiveState];
    }
  }

  // ---------------------------------------------------------------- #
  // Not-needed switches
  // ---------------------------------------------------------------- #

  /**
   * Save one product's not-needed switch for the park, then tell the wizard.
   */
  const handleNotNeededToggle = async (productKey, notNeeded) => {
    setSavingProduct(productKey);
    setSwitchError(null);
    try {
      // Save the exclusion; the API recomputes the register status
      await setExclusion(park.parkiId, productKey, notNeeded);
      setNotNeededOverrides(previous => ({ ...previous, [productKey]: notNeeded }));
      if (onNotNeededChange) {
        onNotNeededChange(productKey, notNeeded);
      }
    } catch (err) {
      setSwitchError(err.message || 'That could not be saved. Please try again.');
    } finally {
      setSavingProduct(null);
    }
  };

  // One switch per product whose cell the register lets us change
  const switches = [];
  for (const row of productRows) {
    const isLocked = LOCKED_STATES.includes(row.cell.state);
    if (isLocked) {
      continue;
    }
    const isOn = row.effectiveState === 'not-needed';
    const isSaving = savingProduct === row.productKey;
    const switchId = `integrations-not-needed-${row.productKey}`;
    switches.push(
      <div key={row.productKey} className="form-check form-switch mb-0">
        <input
          id={switchId}
          className="form-check-input"
          type="checkbox"
          role="switch"
          checked={isOn}
          disabled={isSaving || busy}
          onChange={(event) => handleNotNeededToggle(row.productKey, event.target.checked)}
        />
        <label className="form-check-label" htmlFor={switchId}>{PRODUCT_LABELS[row.productKey] || row.productKey}</label>
      </div>
    );
  }
  const showSwitches = switches.length > 0;

  // ---------------------------------------------------------------- #
  // Stepper
  // ---------------------------------------------------------------- #

  /**
   * Move to a step the user clicked, if they have reached it already.
   */
  const handleStepClick = (stepNumber) => (event) => {
    event.preventDefault();

    // Only steps already reached can be clicked, and not while saving
    const stepReached = stepNumber <= maxReachedStep;
    if (!stepReached || busy) {
      return;
    }

    // Tell the wizard which step to show
    if (onStepChange) {
      onStepChange(stepNumber);
    }
  };

  // One bar per step: filled up to the current step, clickable once reached
  const stepBars = [];
  for (let index = 0; index < steps.length; index++) {
    const stepNumber = index + 1;
    const isReached = stepNumber <= maxReachedStep;

    // Work out the bar's look
    let barClass = 'integrations-stepper-bar';
    if (stepNumber <= step) {
      barClass += ' is-filled';
    } else if (isReached) {
      barClass += ' is-reached';
    }

    stepBars.push(
      <button
        key={`step-${stepNumber}`}
        type="button"
        className={barClass}
        onClick={handleStepClick(stepNumber)}
        disabled={!isReached || busy}
        aria-current={stepNumber === step ? 'step' : undefined}
        aria-label={`Step ${stepNumber}: ${steps[index]}`}
        title={steps[index]}
      ></button>
    );
  }

  // Only show a stepper when the wizard has more than one step
  const showStepper = steps.length > 1;
  const stepCaption = `Step ${step} of ${steps.length}: ${steps[step - 1] || ''}`;

  // ---------------------------------------------------------------- #
  // Footer buttons
  // ---------------------------------------------------------------- #

  // Back is shown from step 2 onwards unless the wizard hides it
  const showBack = step > 1 && !hideBack;

  /**
   * Go back one step.
   */
  const handleBack = () => {
    if (onStepChange) {
      onStepChange(step - 1);
    }
  };

  // The primary button is shown only when the wizard gives it a label
  const showPrimary = Boolean(primaryLabel);

  // The heading: the park's name when opened from a park, otherwise the provider
  let headingText = title;
  let captionText = null;
  if (park) {
    headingText = park.parkName;
    captionText = title;
  }

  // The error to show: the wizard's own, else one from a switch
  let shownError = error;
  if (!shownError) {
    shownError = switchError;
  }

  // ---------------------------------------------------------------- #
  // Render
  // ---------------------------------------------------------------- #

  return (
    <>
      <div className="modal fade show integrations-wizard" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-dialog modal-xl modal-dialog-scrollable modal-fullscreen-lg-down" role="document">
          <div className="modal-content">

            {/* Header: logo, park, status and close */}
            <div className="modal-header integrations-wizard-header">
              {logoSrc && (
                <img src={logoSrc} alt="" className="integrations-wizard-logo" />
              )}
              <div className="integrations-wizard-heading">
                <h5 className="modal-title mb-0">{headingText}</h5>
                {captionText && <div className="integrations-wizard-caption">{captionText}</div>}
              </div>
              {pill && (
                <span className={`integrations-status-pill ${pill.toneClass}`}>{pill.label}</span>
              )}
              <button type="button" className="btn-close ms-2" onClick={requestClose} aria-label="Close"></button>
            </div>

            {/* Stepper: thin bars and a caption */}
            {showStepper && (
              <div className="integrations-stepper">
                <div className="integrations-stepper-bars">
                  {stepBars}
                </div>
                <div className="integrations-stepper-caption">{stepCaption}</div>
              </div>
            )}

            {/* Step content, with any error first */}
            <div className="modal-body integrations-wizard-body">
              {shownError && (
                <div className="alert alert-danger d-flex align-items-start gap-2" role="alert">
                  <i className="icon-base ti tabler-alert-circle"></i>
                  <span>{shownError}</span>
                </div>
              )}
              {children}
            </div>

            {/* Footer strip: not-needed switches, then the buttons */}
            <div className="modal-footer integrations-wizard-footer">
              <div className="integrations-wizard-footer-start">
                {showSwitches && (
                  <>
                    <span className="integrations-not-needed-label">Not needed for this park:</span>
                    {switches}
                  </>
                )}
                {footerStart}
              </div>
              <div className="d-flex gap-2">
                {showBack && (
                  <button type="button" className="btn btn-label-secondary" onClick={handleBack} disabled={busy}>
                    Back
                  </button>
                )}
                {showPrimary && (
                  <button type="button" className="btn btn-primary" onClick={onPrimary} disabled={primaryDisabled || busy}>
                    {busy && (
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    )}
                    {primaryLabel}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
};

export default WizardFrame;
